import { Fragment, useState } from "react";
import { Dialog, Transition, Disclosure } from "@headlessui/react";
import {
  Bars3Icon,
  CalendarIcon,
  ChartPieIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  UsersIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  ShoppingCartIcon,
  ChevronRightIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowRightOnRectangleIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import { getUser } from "../utils/storage";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../Pages/Layout/Footer";
import GlobalDeviceSearch from "./GlobalSearch";
import useDialogStore from "../stores/useDialogStore";
import ReserveMultipleDevices from "./ReserveMultipleDevices";
import { useEffect } from "react";
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
const user = getUser();
const userRole = user?.role;
// Unified blue theme for all roles
const sidebarBgColor = "bg-bluePrimary";
const textColor = "text-white";
const hoverBgColor = "hover:bg-blue-600";
const activeBgColor = "bg-white";
const activeTextColor = "text-bluePrimary";

export default function AdminSidebar(props) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { openDialog } = useDialogStore();

  const navigate = useNavigate();
  // master route catalog: define once, reuse per-role by name
  const ALL_ROUTES = [
    { name: "Dashboard", href: "/admin/dashboard", icon: ChartPieIcon },
    { name: "Device List", href: "/admin/devices", icon: PrinterIcon },
    {
      name: "White Stock",
      href: "/admin/devices/white-stock",
      icon: PrinterIcon,
    },
    {
      name: "Green Stock",
      href: "/admin/devices/green-stock",
      icon: PrinterIcon,
    },
    {
      name: "Blue Stock",
      href: "/admin/devices/blue-stock",
      icon: PrinterIcon,
    },
    {
      name: "Toner Stock",
      href: "/admin/toner-stock",
      icon: PrinterIcon,
    },
    {
      name: "Reservations",
      icon: CalendarIcon,
      children: [
        {
          name: "Reserved Stock",
          href: "/admin/stock/reserved",
          icon: CalendarIcon,
        },
        {
          name: "Reserve Stock",
          href: "/admin/stock/reserve",
          icon: CheckIcon,
        },
        {
          name: "Reserve Multiple Devices by Unique ID",
          icon: PlusIcon,
          onClick: () => {
            openDialog({
              title: "Reserve Multiple Devices by Unique ID",
              content: <ReserveMultipleDevices />,
            });
          },
        },
      ],
    },
    { name: "Sold Stock", href: "/admin/stock/sold", icon: ShoppingCartIcon },
    {
      name: "Recycled Stock",
      href: "/admin/stock/history/recycled",
      icon: DocumentDuplicateIcon,
    },
    {
      name: "Spare Parts Stock",
      href: "/admin/spare-parts",
      icon: DocumentDuplicateIcon,
    },
    {
      name: "Total Quantity Report",
      href: "/admin/reports/total-quantity",
      icon: ChartPieIcon,
    },
    {
      name: "Client Report",
      href: "/admin/reports/client",
      icon: ChartPieIcon,
    },
    {
      name: "Reports",
      icon: ChartPieIcon,
      children: [
        {
          name: "Green Stock Report",
          href: "/admin/reports/green",
          icon: ChartPieIcon,
        },
        {
          name: "Blue Stock Report",
          href: "/admin/reports/blue",
          icon: ChartPieIcon,
        },
        {
          name: "White Stock Report",
          href: "/admin/reports/white",
          icon: ChartPieIcon,
        },
        {
          name: "Recycled Stock Report",
          href: "/admin/reports/recycled",
          icon: ChartPieIcon,
        },
        {
          name: "Reserved Stock Report",
          href: "/admin/reports/reserved",
          icon: ChartPieIcon,
        },
        {
          name: "Sold Stock Report",
          href: "/admin/reports/sold",
          icon: ChartPieIcon,
        },
        {
          name: "Invoice Report",
          href: "/admin/reports/invoice",
          icon: ChartPieIcon,
        },
      ],
    },
    {
      name: "Models",
      icon: FolderIcon,
      children: [
        {
          name: "Device models",
          href: "/admin/device-models",
          icon: FolderIcon,
        },
        {
          name: "Toner models",
          href: "/admin/toner-models",
          icon: FolderIcon,
        },
      ],
    },
    { name: "Brands", href: "/admin/device-brands", icon: FolderIcon },
    {
      name: "Users",
      href: "/admin/users",
      icon: UsersIcon,
    },
    {
      name: "Rentals",
      icon: DocumentDuplicateIcon,
      children: [
        {
          name: "Rented Devices",
          href: "/admin/stock/rented",
          icon: DocumentDuplicateIcon,
        },
        {
          name: "Rent Devices",
          href: "/admin/stock/rent",
          icon: PlusIcon,
        },
        {
          name: "Inactive Rentals",
          href: "/admin/stock/inactive-rentals",
          icon: DocumentDuplicateIcon,
        },
        {
          name: "Payments History",
          href: "/admin/rentals/payments",
          icon: DocumentDuplicateIcon,
        },
      ],
    },
    {
      name: "Clients",
      href: "/admin/clients",
      icon: UsersIcon,
    },
  ];

  const ROLE_ROUTE_NAMES = {
    admin: [
      "Dashboard",
      "Device List",
      "White Stock",
      "Green Stock",
      "Blue Stock",
      "Toner Stock",
      "Reservations",
      "Sold Stock",
      "Recycled Stock",
      "Spare Parts Stock",
      "Total Quantity Report",
      "Client Report",
      "Reports",
      "Models",
      "Brands",
      "Users",
      "Rentals",
      "Clients",
    ],
    superadmin: [
      "Dashboard",
      "Device List",
      "White Stock",
      "Green Stock",
      "Blue Stock",
      "Toner Stock",
      "Reservations",
      "Sold Stock",
      "Recycled Stock",
      "Spare Parts Stock",
      "Total Quantity Report",
      "Client Report",
      "Reports",
      "Models",
      "Brands",
      "Users",
      "Rentals",
      "Clients",
    ],
    "stock manager": [
      "Dashboard",
      "Device List",
      "White Stock",
      "Green Stock",
      "Blue Stock",
      "Toner Stock",
      "Reservations",
      "Sold Stock",
      "Recycled Stock",
      "Spare Parts Stock",
      "Total Quantity Report",
      "Client Report",
      "Reports",
      "Models",
      "Brands",
      "Users",
      "Rentals",
      "Clients",
    ],
    technician: [
      "Register Device",
      "Device List",
      "White Stock",
      "Green Stock",
      "Blue Stock",
      "Toner Stock",
      "Recycled Stock",
      "Spare Parts Stock",
      "Brands",
      "Models",
    ],
  };

  function findRouteByName(routeName) {
    const deepFind = (items) => {
      for (const item of items) {
        if (item.name === routeName) return item;
        if (item.children) {
          const found = deepFind(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return deepFind(ALL_ROUTES);
  }

  function buildNavigationForRole(normalized) {
    const names = ROLE_ROUTE_NAMES[normalized] || [];
    return names
      .map((name) => findRouteByName(name))
      .filter(Boolean)
      .map((item) => ({ ...item }));
  }

  // per-role navigation will be built from names using the catalog

  const location = useLocation();

  // Handle keyboard shortcut
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if ((e.ctrlKey || e.metaKey) && e.key === "k") {
  //       e.preventDefault();
  //       setSearchOpen(true);
  //     }
  //     if (e.key === "Escape") {
  //       setSearchOpen(false);
  //     }
  //   };

  //   document.addEventListener("keydown", handleKeyDown);
  //   return () => document.removeEventListener("keydown", handleKeyDown);
  // }, []);

  let navigation = [];

  const normalizedRole = userRole?.toLowerCase();
  if (normalizedRole) {
    const roleNav = buildNavigationForRole(normalizedRole);
    navigation = roleNav.map((item) => ({
      ...item,
      current: item.href === location.pathname,
    }));
  }

  function handleLogOut() {
    console.log("log out");
    localStorage.removeItem("user");
    localStorage.removeItem("api_token");
    window.location.assign("/login");
  }

  useEffect(() => {
    console.log("normalizedRole");
  }, []);

  return (
    <>
      <div className="min-h-full">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div
                    className={`flex grow flex-col gap-y-5 overflow-y-auto ${sidebarBgColor} px-6 pb-4 ring-1 ring-white/10`}
                  >
                    <div className="flex h-16 shrink-0 items-center">
                      <h3
                        onClick={() => navigate("/admin/dashboard")}
                        className="text-3xl  font-bold text-white cursor-pointer"
                      >
                        RitexApp
                      </h3>
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                {!item.children ? (
                                  item.onClick ? (
                                    <button
                                      onClick={item.onClick}
                                      className={classNames(
                                        item.current
                                          ? `${activeTextColor} ${activeBgColor}`
                                          : `${textColor} hover:text-white ${hoverBgColor}`,
                                        "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition duration-200 ease-in-out w-full text-left"
                                      )}
                                    >
                                      <item.icon
                                        className="h-6 w-6 shrink-0"
                                        aria-hidden="true"
                                      />
                                      {item.name}
                                    </button>
                                  ) : (
                                    <a
                                      href={item.href}
                                      className={classNames(
                                        item.current
                                          ? `${activeTextColor} ${activeBgColor}`
                                          : `${textColor} hover:text-white ${hoverBgColor}`,
                                        "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition duration-200 ease-in-out"
                                      )}
                                    >
                                      <item.icon
                                        className="h-6 w-6 shrink-0"
                                        aria-hidden="true"
                                      />
                                      {item.name}
                                    </a>
                                  )
                                ) : (
                                  <Disclosure
                                    defaultOpen={item.children.some(
                                      (subItem) =>
                                        location.pathname === subItem.href
                                    )}
                                  >
                                    {({ open }) => (
                                      <>
                                        <Disclosure.Button
                                          className={classNames(
                                            `${textColor} hover:text-white ${hoverBgColor} transition duration-300 ease-in-out`,
                                            "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                            item.children.some(
                                              (subItem) =>
                                                location.pathname ===
                                                subItem.href
                                            )
                                              ? "bg-white/20"
                                              : ""
                                          )}
                                        >
                                          <item.icon
                                            className="h-6 w-6 shrink-0"
                                            aria-hidden="true"
                                          />
                                          {item.name}
                                          <ChevronRightIcon
                                            className={classNames(
                                              open ? "rotate-90 transform" : "",
                                              "h-5 w-5 ml-auto transition-transform duration-200"
                                            )}
                                          />
                                        </Disclosure.Button>
                                        <Disclosure.Panel className="pl-6 pt-1">
                                          {item.children.map((subItem) =>
                                            subItem.onClick ? (
                                              <button
                                                key={subItem.name}
                                                onClick={subItem.onClick}
                                                className={classNames(
                                                  location.pathname ===
                                                    subItem.href
                                                    ? `${activeTextColor} ${activeBgColor}`
                                                    : `${textColor} hover:text-white ${hoverBgColor} transition duration-200 ease-in-out`,
                                                  "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold cursor-pointer w-full text-left"
                                                )}
                                              >
                                                <subItem.icon
                                                  className="h-6 w-6 shrink-0"
                                                  aria-hidden="true"
                                                />
                                                {subItem.name}
                                              </button>
                                            ) : (
                                              <a
                                                key={subItem.name}
                                                onClick={() =>
                                                  navigate(subItem.href)
                                                }
                                                className={classNames(
                                                  location.pathname ===
                                                    subItem.href
                                                    ? `${activeTextColor} ${activeBgColor}`
                                                    : `${textColor} hover:text-white ${hoverBgColor} transition duration-200 ease-in-out`,
                                                  "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold cursor-pointer"
                                                )}
                                              >
                                                <subItem.icon
                                                  className="h-6 w-6 shrink-0"
                                                  aria-hidden="true"
                                                />
                                                {subItem.name}
                                              </a>
                                            )
                                          )}
                                        </Disclosure.Panel>
                                      </>
                                    )}
                                  </Disclosure>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>

                        <div className="mt-auto">
                          <li key={user.username} className="flex">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold leading-6 text-gray-300">
                                {user.name}
                              </p>
                              <p className="mt-1 truncate text-xs font-semibold leading-6 text-gray-400">
                                {user.username}
                              </p>
                            </div>
                          </li>
                          <li>
                            {userRole === "Admin" ||
                              (userRole === "SuperAdmin" && (
                                <a
                                  href={
                                    userRole === "SuperAdmin"
                                      ? "/admin/trash"
                                      : "/admin/trash/devices"
                                  }
                                  className="group -mx-2 flex gap-x-3 rounded-md p-2  text-sm font-semibold leading-6 text-white hover:bg-red-600 hover:text-white transition duration-200 ease-in-out"
                                >
                                  <TrashIcon className="h-6 w-6 shrink-0" />
                                  Trash
                                </a>
                              ))}
                            <a
                              onClick={handleLogOut}
                              className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-red-600 hover:text-white transition duration-200 ease-in-out"
                            >
                              <ArrowRightOnRectangleIcon
                                className="h-6 w-6 shrink-0"
                                aria-hidden="true"
                              />
                              Log Out
                            </a>
                          </li>
                        </div>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div
            className={`flex grow flex-col gap-y-5 overflow-y-auto ${sidebarBgColor} px-6 pb-4 pt-10 shadow-xl`}
          >
            <div className="flex h-16 shrink-0 items-center">
              <h3
                onClick={() => navigate("/admin/dashboard")}
                className="text-3xl font-bold text-white cursor-pointer"
              >
                RitexApp
              </h3>
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        {!item.children ? (
                          item.onClick ? (
                            <button
                              onClick={item.onClick}
                              className={classNames(
                                item.current
                                  ? `${activeTextColor} ${activeBgColor}`
                                  : `${textColor} hover:text-white ${hoverBgColor} transition duration-200 ease-in-out`,
                                "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full text-left"
                              )}
                            >
                              <item.icon
                                className="h-6 w-6 shrink-0"
                                aria-hidden="true"
                              />
                              {item.name}
                            </button>
                          ) : (
                            <a
                              href={item.href}
                              className={classNames(
                                item.current
                                  ? `${activeTextColor} ${activeBgColor}`
                                  : `${textColor} hover:text-white ${hoverBgColor} transition duration-200 ease-in-out`,
                                "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold"
                              )}
                            >
                              <item.icon
                                className="h-6 w-6 shrink-0"
                                aria-hidden="true"
                              />
                              {item.name}
                            </a>
                          )
                        ) : (
                          <Disclosure
                            defaultOpen={item.children.some(
                              (subItem) => location.pathname === subItem.href
                            )}
                          >
                            {({ open }) => (
                              <>
                                <Disclosure.Button
                                  className={classNames(
                                    `${textColor} hover:text-white ${hoverBgColor} transition duration-300 ease-in-out`,
                                    "group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                                    item.children.some(
                                      (subItem) =>
                                        location.pathname === subItem.href
                                    )
                                      ? "bg-white/20"
                                      : ""
                                  )}
                                >
                                  <item.icon
                                    className="h-6 w-6 shrink-0"
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                  <ChevronRightIcon
                                    className={classNames(
                                      open ? "rotate-90 transform" : "",
                                      "h-5 w-5 ml-auto transition-transform duration-200"
                                    )}
                                  />
                                </Disclosure.Button>
                                <Disclosure.Panel className="pl-6 pt-1">
                                  {item.children.map((subItem) =>
                                    subItem.onClick ? (
                                      <button
                                        key={subItem.name}
                                        onClick={subItem.onClick}
                                        className={classNames(
                                          location.pathname === subItem.href
                                            ? `${activeTextColor} ${activeBgColor}`
                                            : `${textColor} hover:text-white ${hoverBgColor} transition duration-200 ease-in-out`,
                                          "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold cursor-pointer w-full text-left"
                                        )}
                                      >
                                        <subItem.icon
                                          className="h-6 w-6 shrink-0"
                                          aria-hidden="true"
                                        />
                                        {subItem.name}
                                      </button>
                                    ) : (
                                      <a
                                        key={subItem.name}
                                        onClick={() => navigate(subItem.href)}
                                        className={classNames(
                                          location.pathname === subItem.href
                                            ? `${activeTextColor} ${activeBgColor}`
                                            : `${textColor} hover:text-white ${hoverBgColor} transition duration-200 ease-in-out`,
                                          "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold cursor-pointer"
                                        )}
                                      >
                                        <subItem.icon
                                          className="h-6 w-6 shrink-0"
                                          aria-hidden="true"
                                        />
                                        {subItem.name}
                                      </a>
                                    )
                                  )}
                                </Disclosure.Panel>
                              </>
                            )}
                          </Disclosure>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>

                <div className="mt-auto">
                  <li key={user.username} className="flex">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-5 text-gray-300">
                        {user.name} {user.last_name}
                      </p>
                      <p className="mt-1 truncate text-xs font-semibold leading-5 text-gray-400">
                        {user.role}
                      </p>
                    </div>
                  </li>

                  <li>
                    {(userRole === "Admin" || userRole === "SuperAdmin") && (
                      <a
                        href={
                          userRole === "SuperAdmin"
                            ? "/admin/trash"
                            : "/admin/trash/devices"
                        }
                        className="group -mx-2 flex gap-x-3 rounded-md p-2 mt-2 text-sm font-semibold leading-6 text-white hover:bg-white hover:text-bluePrimary transition duration-200 ease-in-out"
                      >
                        <TrashIcon className="h-6 w-6 shrink-0" />
                        Trash
                      </a>
                    )}
                    <a
                      onClick={handleLogOut}
                      className="group -mx-2 flex mt-2 gap-x-3 cursor-pointer rounded-md p-2 text-sm font-semibold leading-6 text-white hover:bg-white hover:text-bluePrimary transition duration-200 ease-in-out"
                    >
                      <ArrowRightOnRectangleIcon
                        className="h-6 w-6 shrink-0"
                        aria-hidden="true"
                      />
                      Log Out
                    </a>
                  </li>
                </div>
              </ul>
            </nav>
          </div>
        </div>
        <div className="lg:pl-72 relative bg-bgBlue min-h-screen flex-1 pt-5 ">
          <GlobalDeviceSearch />

          <button
            type="button"
            className="fixed top-7 right-7 text-gray-700 z-50 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <main className="py-20 flex-grow flex flex-col">
            <div className="px-4 mb-10">{props.children}</div>
            <Footer />
          </main>
        </div>
      </div>
    </>
  );
}
